import React from "react";
import dayjs from "dayjs";

import { Avatar, Money, OnlineStatus } from "../../components";
import { Icon } from "../../components/ui";
import { getBaseCurrency } from "../../core/currency";
import { displayName, statusMeta } from "./Row.jsx";
import "./users-directory-list.scss";

// Fontaine M54E — the `/users/:type?` directory is a LIST, not a table.
// Guillaume: "the rest of the user table will have to be completely redone and
// redesigned so that it doesn't show as a table this way." So: one full-width
// row per person — avatar, identity, the type's own meta, status, money —
// no column headers, no banding, no grid lines.
//
// The whole row is one button: a person is one destination (the detail drawer),
// so there is nothing else on the row to click, and that keeps the row a single
// tab stop with a single accessible name instead of a table cell grid to
// arrow through.
//
// Every field the table showed is still here, per type — see
// tests/views/UsersDirectoryTableContract.test.jsx, which pins them.

const fmtDate = (value) => (value ? dayjs(value).format("DD MMM YYYY") : "—");

// `last_purchase` is one of the directory's own sort keys, so the field rides on
// the search hit — under one of two names depending on the endpoint version.
// Absent is a real answer here (a client who has never bought), and reads as "—".
// The last invoice on the client's most recent paid order. Both shapes are
// accepted because the field is not on `/api/users/search` yet (verified against
// the live payload 2026-08-31) — the column renders "—" until it is, rather than
// pretending the data is there.
const lastInvoice = (contact) => {
  const src = contact.last_invoice || {};
  const url = contact.last_invoice_url || src.url || null;
  const thumb =
    contact.last_invoice_thumb_url || src.thumb_url || src.preview_url || null;
  return url || thumb ? { url, thumb } : null;
};

const lastPurchase = (contact) =>
  contact.last_purchase || contact.last_purchase_at || contact.last_order_at || null;

// The one line of type-specific meta: a merchant is placed by its store, an
// expert by their role. A client has neither, and gets nothing rather than a
// column of dashes.
const metaFor = (contact, type) => {
  if (type === "merchant" || type === "supplier") {
    return [contact.store?.name || "—", contact.store_id || "—"];
  }
  if (type === "expert") {
    return [contact.title || "—", contact.company || "—"];
  }
  return null;
};

const DirectoryRow = ({ contact, type, selected, onOpen }) => {
  const name = displayName(contact);
  const status = statusMeta(contact);
  const meta = metaFor(contact, type);
  const showsTurnover = type !== "merchant" && type !== "supplier";
  const invoice = lastInvoice(contact);

  return (
    <li className={`users-list__item${selected ? " users-list__item--selected" : ""}`}>
      {/* The row is a grid with ONE full-bleed button over it, rather than a
          button wrapping everything: the invoice thumbnail is a second control
          (it opens the document, not the person) and a button cannot nest inside
          a button. The overlay keeps the row a single tab stop with a single
          accessible name, exactly as before. */}
      <div className="users-list__row">
        <button
          type="button"
          className="users-list__open"
          aria-label={`Open ${name}`}
          onClick={() => onOpen?.(contact)}
        />
        <span className="users-list__avatar avatar-with-status">
          <Avatar
            fileId={contact?.avatar_file_id}
            fallback="/assets/default-avatar-dark.svg"
          />
          <OnlineStatus lastActiveDate={contact?.user?.last_active_at} />
        </span>

        {/* The CARD's copy of the status, under the avatar. A card is a column,
            so the pill on the name line pushed a long name into an ellipsis to
            make room for it; under the avatar it has the width it needs and the
            name gets the whole line (Alex, 2026-09-25).

            Rendered twice and hidden by breakpoint rather than moved, because
            one DOM serves both the table row and the card. The hidden copy is
            display:none, so it is out of the accessible name too and a row is
            never announced with its status twice. */}
        <span className="users-list__status-card">
          <span className={`users-status users-status--${status.tone}`}>
            <span className="users-status__dot" />
            {status.label}
          </span>
        </span>

        <span className="users-list__identity">
          {/* Status sits ON the name line, not in a track of its own. It says something ABOUT
              this client — turnover / last purchase / joined are just record fields — and given
              its own grid column it drifted to the far right of a short name instead of
              reading as that person's state. */}
          <span className="users-list__nameline">
            <span className="users-list__name">{name}</span>
            <span className={`users-status users-status--${status.tone}`}>
              <span className="users-status__dot" />
              {status.label}
            </span>
          </span>
          {/* Email only. The phone has its own column to the right, and printing it here too
              made the identity line the widest thing in the row for no extra information. */}
          <span className="users-list__sub">
            <span>{contact.email || `ID ${contact.id}`}</span>
          </span>

          {/* The narrow layouts' copy of the phone, directly under the email.
              It has a column of its own on a wide row; down here that column was
              a full-width line adrift in the facts block, and on a contact with
              no phone it was a bare em dash on a line by itself (Alex,
              2026-09-25). Rendered only when there IS one, for that reason. */}
          {contact.phone ? (
            <span className="users-list__sub users-list__phone">{contact.phone}</span>
          ) : null}
        </span>

        {meta ? (
          <span className="users-list__meta">
            <span>{meta[0]}</span>
            <span className="users-list__sub">{meta[1]}</span>
          </span>
        ) : null}

        {/* Four fixed-width, centred columns — one per fact, so the values line
            up down the list instead of drifting with the text beside them.
            Turnover, last purchase and joined were two crushed lines in one
            right-aligned block before. Each carries its own caption: a list has
            no header row to name its columns. */}
        <span className="users-list__cols">
          {/* No caption: a phone number names itself. The other columns need one because
              "Turnover" and "Last purchase" are indistinguishable as bare values; a number
              starting +44 is not. `--cap-less` keeps it on the shared baseline anyway. */}
          <span className="users-list__col users-list__col--capless">
            <span className="users-list__val">{contact.phone || "—"}</span>
          </span>

          {showsTurnover ? (
            <span className="users-list__col">
              <span className="users-list__val">
                {/* TODO(currency): turnover is a cross-record aggregate with no
                    per-record currency — deliberate store-base render. */}
                <Money amount={contact.turnover} currency={getBaseCurrency()} zero="—" />
              </span>
              <span className="users-list__cap">Turnover</span>
            </span>
          ) : null}

          <span className="users-list__col">
            <span className="users-list__val">{fmtDate(lastPurchase(contact))}</span>
            <span className="users-list__cap">Last purchase</span>
          </span>

          <span className="users-list__col">
            {invoice ? (
              <button
                type="button"
                className="users-list__invoice"
                aria-label={`Open the last invoice for ${name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(invoice.url || invoice.thumb, "_blank", "noopener,noreferrer");
                }}
              >
                {invoice.thumb ? (
                  <img src={invoice.thumb} alt="" />
                ) : (
                  <Icon name="FileText" size="sm" />
                )}
              </button>
            ) : (
              <span className="users-list__val">—</span>
            )}
            <span className="users-list__cap">Last invoice</span>
          </span>

          <span className="users-list__col">
            <span className="users-list__val">{fmtDate(contact.created_at)}</span>
            <span className="users-list__cap">Joined</span>
          </span>
        </span>

        <Icon name="ChevronRight" size="xs" className="users-list__chevron" />
      </div>
    </li>
  );
};

/**
 * `view` is presentation only. The card view renders the SAME `DirectoryRow` markup and is laid
 * out by CSS — a second card component would be a second place for a field to be added, and the
 * two views would show different things within a release. The contract test that pins which
 * fields a row carries therefore covers both views for free.
 */
const DirectoryList = ({ contacts, type, noun, selectedContactId, onOpenContact, view = "list" }) => (
  <ul
    className={`users-list${view === "grid" ? " users-list--grid" : ""}`}
    aria-label={`${noun} directory`}
  >
    {contacts.map((contact) => (
      <DirectoryRow
        key={contact.id}
        contact={contact}
        type={type}
        selected={selectedContactId != null && selectedContactId === contact.id}
        onOpen={onOpenContact}
      />
    ))}
  </ul>
);

export default DirectoryList;
